/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/hooks/useConfirmDialog.ts
 * layer: web
 * area: hooks
 * kind: hook
 * web_root: hooks
 * relpath: web/src/platform/design-system/hooks/useConfirmDialog.ts
 * filename: useConfirmDialog.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

/**
 * useConfirmDialog Hook
 *
 * Provides a Promise-based API for showing confirmation dialogs.
 * Returns a `confirm` function and a `ConfirmDialogElement` to render.
 *
 * Usage:
 *   const { confirm, ConfirmDialogElement } = useConfirmDialog();
 *
 *   // In your handler:
 *   const confirmed = await confirm({
 *     title: 'Delete Ticket',
 *     message: 'Are you sure you want to delete this ticket?',
 *     variant: 'danger',
 *     confirmLabel: 'Delete',
 *   });
 *   if (confirmed) { deleteTicket(); }
 *
 *   // In your JSX (render ConfirmDialogElement alongside your page content):
 *   return ( ... {ConfirmDialogElement} ... )
 *
 * Tenant-wide or irreversible actions: pass `typedConfirmation` (e.g. the
 * tenant or policy name); Confirm stays disabled until it is typed exactly.
 *
 * Replacing window.prompt (a reason, a name):
 *   const reason = await promptText({
 *     title: 'Cancel orders',
 *     message: 'The vendor is sent this reason.',
 *     label: 'Reason',
 *   });
 *   if (reason !== null) cancel(reason);
 */

import { useState, useCallback, useRef, createElement, type ReactNode } from 'react';
import { ConfirmDialog } from '@/platform/design-system/components/shared/ConfirmDialog';
import type { ConfirmDialogVariant } from '@/platform/design-system/components/shared/ConfirmDialog';

export interface ConfirmOptions {
  /** Dialog title */
  title: string;
  /** Dialog message / description */
  message: ReactNode;
  /** Visual variant (default: 'danger') */
  variant?: ConfirmDialogVariant;
  /** Confirm button label (default: 'Confirm') */
  confirmLabel?: string;
  /** Cancel button label (default: 'Cancel') */
  cancelLabel?: string;
  /**
   * Text the user must type exactly before Confirm is enabled (typed
   * confirmation for tenant-wide or irreversible actions).
   */
  typedConfirmation?: string;
}

export interface PromptOptions {
  title: string;
  message?: ReactNode;
  /** Field label (default: 'Reason'). */
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  /** When true (default), Confirm is disabled until the field has text. */
  required?: boolean;
  /** Textarea (default) or single-line input. */
  multiline?: boolean;
  /** Visual variant (default: 'warning') */
  variant?: ConfirmDialogVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

type Mode =
  | { kind: 'confirm'; typed?: string }
  | { kind: 'prompt'; label: string; placeholder?: string; required: boolean; multiline: boolean };

interface DialogState {
  open: boolean;
  title: string;
  message: ReactNode;
  variant: ConfirmDialogVariant;
  confirmLabel: string;
  cancelLabel: string;
  mode: Mode;
}

const initialState: DialogState = {
  open: false,
  title: '',
  message: '',
  variant: 'danger',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  mode: { kind: 'confirm' },
};

const FIELD_CLASS =
  'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg-secondary))] px-3 py-2 text-sm text-[rgb(var(--color-text-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring';

export function useConfirmDialog() {
  const [state, setState] = useState<DialogState>(initialState);
  const [value, setValue] = useState('');
  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  const open = useCallback((next: Omit<DialogState, 'open'>, initialValue = '') => {
    // A dialog opened while another is pending cancels the earlier one.
    resolveRef.current?.(null);
    return new Promise<string | null>((resolve) => {
      resolveRef.current = resolve;
      setValue(initialValue);
      setState({ ...next, open: true });
    });
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      open({
        title: options.title,
        message: options.message,
        variant: options.variant ?? 'danger',
        confirmLabel: options.confirmLabel ?? 'Confirm',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        mode: { kind: 'confirm', typed: options.typedConfirmation },
      }).then((result) => result !== null),
    [open]
  );

  const promptText = useCallback(
    (options: PromptOptions): Promise<string | null> =>
      open(
        {
          title: options.title,
          message: options.message ?? '',
          variant: options.variant ?? 'warning',
          confirmLabel: options.confirmLabel ?? 'Confirm',
          cancelLabel: options.cancelLabel ?? 'Cancel',
          mode: {
            kind: 'prompt',
            label: options.label ?? 'Reason',
            placeholder: options.placeholder,
            required: options.required ?? true,
            multiline: options.multiline ?? true,
          },
        },
        options.defaultValue ?? ''
      ),
    [open]
  );

  const settle = useCallback((result: string | null) => {
    setState((prev) => ({ ...prev, open: false }));
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(result);
  }, []);

  const { mode } = state;
  const confirmDisabled =
    mode.kind === 'confirm'
      ? Boolean(mode.typed) && value.trim() !== mode.typed
      : mode.required && value.trim() === '';

  const handleConfirm = useCallback(() => {
    if (confirmDisabled) return;
    settle(mode.kind === 'prompt' ? value.trim() : '');
  }, [confirmDisabled, mode.kind, settle, value]);

  const handleCancel = useCallback(() => settle(null), [settle]);

  let field: ReactNode = null;
  if (state.open && mode.kind === 'confirm' && mode.typed) {
    field = createElement(
      'label',
      { className: 'block text-xs text-[rgb(var(--color-text-secondary))]' },
      'Type ',
      createElement('strong', { className: 'font-mono' }, mode.typed),
      ' to confirm',
      createElement('input', {
        'data-autofocus': true,
        className: `${FIELD_CLASS} mt-1`,
        value,
        autoComplete: 'off',
        spellCheck: false,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value),
      })
    );
  } else if (state.open && mode.kind === 'prompt') {
    field = createElement(
      'label',
      { className: 'block text-xs font-medium text-[rgb(var(--color-text-secondary))]' },
      mode.label,
      createElement(mode.multiline ? 'textarea' : 'input', {
        'data-autofocus': true,
        className: `${FIELD_CLASS} mt-1`,
        rows: mode.multiline ? 3 : undefined,
        placeholder: mode.placeholder,
        required: mode.required,
        value,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          setValue(e.target.value),
      })
    );
  }

  const ConfirmDialogElement = createElement(
    ConfirmDialog,
    {
      open: state.open,
      title: state.title,
      message: state.message,
      variant: state.variant,
      confirmLabel: state.confirmLabel,
      cancelLabel: state.cancelLabel,
      confirmDisabled,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
    field
  );

  return { confirm, promptText, ConfirmDialogElement } as const;
}
